<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from '@/components/forms/button/button.vue';
import type { Emits, Props, Slots } from './types';
import Textarea from '@/components/forms/textarea/textarea.vue';

const props = withDefaults(defineProps<Props>(), {
	defaultValue: '',
	cancelText: 'Cancel',
	submitText: 'Send',
	closeText: 'Close',
	expandable: true,
});

defineSlots<Slots>();
const emit = defineEmits<Emits>();

const localValue = ref(props.defaultValue);

const isFocused = ref(props.isFocused);

const isChanged = computed(() => localValue.value !== props.defaultValue);

const submit = (value: string) => {
	emit('submit', value);

	localValue.value = props.defaultValue;
};

const cancel = () => {
	if (props.forceShowCloseBtn && !isChanged.value) {
		emit('close');

		return;
	}

	localValue.value = props.defaultValue;
};

const clickOnTitle = () => {
	if (props.attachToKeyboard) emit('clickOnTitle');
};
</script>

<template>
	<div
		:class="{
			'top-editArea': true,
			'top-editArea-attachedToKeyboard': attachToKeyboard,
		}"
	>
		<div
			v-if="title || $slots.caption"
			class="top-editArea_caption"
			@click="clickOnTitle()"
		>
			<slot name="caption">
				{{ title }}
			</slot>
		</div>

		<div
			:class="{
				'top-editArea_form': true,
				'top-forms-focusable': true,
				'top-as-input': true,
				'top-error': isError,
				'top-focus': isFocused,
			}"
		>
			<Textarea
				v-model="localValue"
				:name="name"
				:placeholder="placeholder"
				:rows="rows"
				:minHeight="minHeight"
				:expandable="expandable"
				:disabled="disabled"
				:readonly="readonly"
				:isError="isError"
				:hint="hint"
				class="top-editArea_element"
				@focus="() => isFocused = true"
				@blur="() => isFocused = false"
				@keyup.esc="cancel"
				@keyup.ctrl.enter="submit(localValue)"
			/>

			<div class="top-editArea_footer">
				<Button
					v-if="isChanged || forceShowCloseBtn"
					:icon="$core.state.isMobile ? '': ''"
					class="top-editArea_button"
					color="theme"
					styling="soft"
					@click="cancel"
				>
					<template
						#default
						v-if="!$core.state.isMobile"
					>
						{{ cancelText }}
					</template>
				</Button>

				<Button
					class="top-editArea_button"
					v-if="isChanged"
					:icon="$core.state.isMobile ? '': ''"
					@click="submit(localValue)"
				>
					<template
						#default
						v-if="!$core.state.isMobile"
					>
						{{ submitText }}
					</template>
				</Button>
			</div>
		</div>
	</div>
</template>

<style>
.top-editArea {
	--top-editArea-bottom: env(keyboard-inset-height, 0px);
	--top-editArea-offset-bottom: 0px;

	display: flex;
	flex-direction: column;
	gap: 6px;
}

.top-editArea_caption {
	font-size: 12px;
}

.top-editArea_form {
	flex-direction: column;
}

/* textarea в EditArea */
.top-textarea {
	width: 100%;
}

.top-editArea_element.top-textarea_textarea {
	--top-forms-border-width: 0px;

	outline: none;
	animation: none;
}

/* footer */
.top-editArea_footer {
	padding: var(--top-forms-padding);
	display: flex;
	min-height: 32px;
	align-self: flex-end;
	justify-content: flex-end;
	gap: var(--top-forms-padding);
}

/* attachedToKeyboard */
.top-editArea-attachedToKeyboard {
	background: var(--top-forms-background-color);
	position: fixed;
	bottom: calc(var(--top-editArea-offset-bottom) + var(--top-editArea-bottom));
	right: 0;
	left: 0;
	z-index: 2;
	gap: 0;
	transition: bottom var(--transition-fast);
}

.top-editArea-attachedToKeyboard .top-editArea_form {
	border-radius: 0;
	border: none;
	border-top: 1px solid var(--top-forms-border-color);
}

.top-editArea-attachedToKeyboard .top-editArea_title {
	cursor: pointer;
	border-top: 1px solid var(--color-line-2-opacity);
	padding: var(--top-forms-padding);
}

.top-editArea-attachedToKeyboard .top-editArea_footer > [data-top-icon] {
	border-radius: 100%;
}

@media screen and (max-width: 900px) {
	.top-editArea_form {
		flex-direction: row;
	}
}
</style>
